import { useCallback, useEffect, useState } from 'react';

import type {
  PerformanceKit,
  PerformanceTargetComparison,
  PerformanceTargetList,
  PerformanceTargetRow,
} from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import { useSettingsAutosave } from '../lib/settings-autosave.tsx';
import {
  canWritePerformanceTargets,
  comparisonOptions,
  TARGET_METRIC_OPTIONS,
} from '../lib/performance-targets.ts';
import { api, type SessionUser } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function downloadText(filename: string, text: string, type = 'text/plain;charset=utf-8'): void {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function PerformanceTargetsPanel({ user }: { user: SessionUser | null }) {
  const store = useStore();
  const state = useLive();
  const canWrite = canWritePerformanceTargets(user);
  const projectKey = state.projectSlug;
  const [page, setPage] = useState<PerformanceTargetList | null>(null);
  const [kit, setKit] = useState<PerformanceKit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!projectKey) {
      setPage(null);
      setKit(null);
      setError(null);
      return;
    }
    try {
      const [targets, nextKit] = await Promise.all([
        api.performanceTargets(projectKey),
        api.performanceKit(projectKey),
      ]);
      setPage(targets);
      setKit(nextKit);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [projectKey]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!projectKey) {
    return (
      <Empty
        title="Select a project."
        body="Performance targets are per-project. They apply only to the performance-change profile."
      />
    );
  }

  if (error && !page) {
    return (
      <Empty
        title="Performance targets could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!page) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className="performance-targets-panel">
      <PerformanceKitPanel kit={kit} onToast={(tone, message) => store.toast(tone, message)} />
      <p className="sheet-note cfg-provenance">
        These numbers are what the performance-change gate compares against after CI publishes
        performance.json. They replace repo <span className="mono">[budgets]</span> for that
        gate. Other profiles are not gated here.
        {canWrite ? null : ' Only an admin can change targets.'}
      </p>
      {page.items.length === 0 ? (
        <p className="sheet-note">
          No project targets yet. The contract default is fps at least 60 until you add a row.
        </p>
      ) : null}
      {page.items.map((row) => (
        <TargetEditor
          key={row.id}
          projectKey={projectKey}
          row={row}
          canWrite={canWrite}
          onSaved={load}
        />
      ))}
      {canWrite ? (
        creating ? (
          <TargetCreateForm
            projectKey={projectKey}
            onCancel={() => setCreating(false)}
            onCreated={async (metric) => {
              setCreating(false);
              store.toast('good', `${metric} saved.`);
              await load();
            }}
            onError={(message) => store.toast('bad', message)}
          />
        ) : (
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            Add a target
          </button>
        )
      ) : null}
    </div>
  );
}

function PerformanceKitPanel({
  kit,
  onToast,
}: {
  kit: PerformanceKit | null;
  onToast: (tone: 'good' | 'bad', message: string) => void;
}) {
  if (!kit) return null;

  return (
    <section className="sheet-panel" data-performance-kit>
      <header className="panel-head">
        <Kicker>Instrument a game</Kicker>
      </header>
      <div className="sheet-panel-body">
        <p className="sheet-note">
          GitLab CI runs the game&apos;s own instrumented mode and publishes{' '}
          <span className="mono">artifacts/performance.json</span>. Nightshift pulls that file
          after the pipeline finishes. It never launches the game. Missing artefact is a skip
          except on the performance-change profile.
        </p>
        <p className="sheet-note">
          Daily collection is automatic after the first valid default-branch performance report.
          Nightshift reuses a daily GitLab schedule or creates one at 03:00 UTC. Paused schedules
          stay paused. Include full performance probes and screenshot grading in nightly CI;
          keep routine merge-request checks fast.
        </p>
        <p className="sheet-note cfg-provenance">
          Copy the prompt and give it to an agent, or download the schema, example, and GitLab
          job and do the wiring yourself.
        </p>
        <div className="reviewers-actions">
          <button
            type="button"
            className="btn btn-accent btn-sm"
            onClick={() =>
              void copyText(kit.agentPrompt).then((ok) =>
                onToast(ok ? 'good' : 'bad', ok ? 'Instrumentation prompt copied.' : 'Copy failed.'),
              )
            }
          >
            Copy agent prompt
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => downloadText('performance-schema.md', kit.schemaMarkdown, 'text/markdown')}
          >
            Download schema
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => downloadText('performance.example.json', kit.exampleJson, 'application/json')}
          >
            Download example
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => downloadText('performance.schema.json', kit.jsonSchema, 'application/json')}
          >
            Download JSON Schema
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => downloadText('gitlab-ci-benchmark.yml', kit.gitlabCiYml, 'text/yaml')}
          >
            Download GitLab job
          </button>
        </div>
      </div>
    </section>
  );
}

function TargetEditor({
  projectKey,
  row,
  canWrite,
  onSaved,
}: {
  projectKey: string;
  row: PerformanceTargetRow;
  canWrite: boolean;
  onSaved: (message: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const persist = useCallback(async (draft: { comparison: PerformanceTargetComparison; value: string }) => {
    await api.updatePerformanceTarget(projectKey, row.metric, {
      comparison: draft.comparison,
      value: Number(draft.value),
    });
    await onSaved('saved');
  }, [onSaved, projectKey, row.metric]);
  const autosave = useSettingsAutosave({
    value: { comparison: row.comparison, value: String(row.value) },
    save: persist,
    what: `the ${row.metric} target`,
    enabled: canWrite,
    delay: 600,
    validate: (draft) => Number.isFinite(Number(draft.value)) ? null : 'Value must be a finite number.',
  });
  const { draft, setDraft, status, error } = autosave;
  const { comparison, value } = draft;

  const remove = async () => {
    setBusy(true);
    try {
      await api.deletePerformanceTarget(projectKey, row.metric);
      await onSaved(`${row.metric} removed.`);
    } catch (caught) {
      setRemoveError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sheet-panel" data-performance-target={row.metric}>
      <header className="panel-head">
        <Kicker>{row.metric}</Kicker>
      </header>
      <div className="sheet-panel-body">
        <TargetFields
          metric={row.metric}
          comparison={comparison}
          value={value}
          canWrite={canWrite}
          metricLocked
          onComparison={(next) => setDraft({ ...draft, comparison: next })}
          onValue={(next) => setDraft({ ...draft, value: next })}
        />
        {canWrite ? (
          <div className="reviewers-actions">
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void remove()}>
              Remove
            </button>
          </div>
        ) : null}
        {status === 'saving' ? <p className="cfg-help">Saving…</p> : null}
        {error ? <p className="cfg-error" role="alert">Unsaved: {error}</p> : null}
        {removeError ? <p className="cfg-error" role="alert">{removeError}</p> : null}
      </div>
    </section>
  );
}

function TargetCreateForm({
  projectKey,
  onCancel,
  onCreated,
  onError,
}: {
  projectKey: string;
  onCancel: () => void;
  onCreated: (metric: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [metric, setMetric] = useState<string>(TARGET_METRIC_OPTIONS[0] ?? 'fps_avg');
  const [comparison, setComparison] = useState<PerformanceTargetComparison>('gte');
  const [value, setValue] = useState('60');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      onError('value must be a finite number');
      return;
    }
    setBusy(true);
    try {
      const created = await api.createPerformanceTarget(projectKey, {
        metric,
        comparison,
        value: parsed,
      });
      await onCreated(created.metric);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sheet-panel" data-performance-target="new">
      <header className="panel-head">
        <Kicker>New target</Kicker>
      </header>
      <div className="sheet-panel-body">
        <TargetFields
          metric={metric}
          comparison={comparison}
          value={value}
          canWrite
          onMetric={setMetric}
          onComparison={setComparison}
          onValue={setValue}
        />
        <div className="reviewers-actions">
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Create'}
          </button>
          <button type="button" className="btn btn-sm" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}

function TargetFields({
  metric,
  comparison,
  value,
  canWrite,
  metricLocked,
  onMetric,
  onComparison,
  onValue,
}: {
  metric: string;
  comparison: PerformanceTargetComparison;
  value: string;
  canWrite: boolean;
  metricLocked?: boolean;
  onMetric?: (value: string) => void;
  onComparison: (value: PerformanceTargetComparison) => void;
  onValue: (value: string) => void;
}) {
  return (
    <div className="reviewers-fields">
      <label className="reviewers-field">
        <span className="cfg-label">Metric</span>
        {metricLocked ? (
          <input className="input" value={metric} disabled />
        ) : (
          <select
            className="input"
            value={metric}
            disabled={!canWrite}
            onChange={(event) => onMetric?.(event.target.value)}
          >
            {TARGET_METRIC_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </label>
      <label className="reviewers-field">
        <span className="cfg-label">Comparison</span>
        <select
          className="input"
          value={comparison}
          disabled={!canWrite}
          onChange={(event) => onComparison(event.target.value as PerformanceTargetComparison)}
        >
          {comparisonOptions().map((option) => (
            <option key={option.id} value={option.id}>
              {option.label} ({option.id})
            </option>
          ))}
        </select>
      </label>
      <label className="reviewers-field">
        <span className="cfg-label">Value</span>
        <input
          className="input"
          value={value}
          disabled={!canWrite}
          inputMode="decimal"
          onChange={(event) => onValue(event.target.value)}
        />
      </label>
    </div>
  );
}
